import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useClub } from '../hooks/useClub';
import { useApi } from '../hooks/useApi';

const LOGO_URL = 'https://murkostorage.blob.core.windows.net/branding/coms-logo.png';

function dataAgeLabel(refreshedAt) {
  if (!refreshedAt) return { label: 'No data', color: '#ef4444' };
  const hours = (Date.now() - new Date(refreshedAt)) / 3600000;
  if (hours < 4)   return { label: `${Math.round(hours * 60)}m ago`,  color: '#22c55e' };
  if (hours < 12)  return { label: `${Math.round(hours)}h ago`,       color: '#22c55e' };
  if (hours < 24)  return { label: `${Math.round(hours)}h ago`,       color: '#f97316' };
  if (hours < 48)  return { label: `${Math.round(hours)}h ago`,       color: '#ef4444' };
  return               { label: `${Math.floor(hours/24)}d ago`,       color: '#ef4444' };
}

export default function Topbar() {
  const { user, logout, role } = useAuth();
  const { currentClub, setCurrentClub, availableClubs, showClubSelector } = useClub();
  const { get } = useApi();
  const [freshness, setFreshness] = useState(null);

  useEffect(() => {
    if (!currentClub?.id) return;
    get(`/data/freshness?club=${currentClub.id}`)
      .then(d => setFreshness(d?.freshness?.[0]?.refreshed_at || null))
      .catch(() => setFreshness(null));
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const age = dataAgeLabel(freshness);

  return (
    <header className="topbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', height: 56, flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        {/* Logo */}
        <img
          src={LOGO_URL}
          alt="COMS"
          style={{ height: 36, width: 'auto', flexShrink: 0 }}
        />
        {/* Tagline + club selector stacked */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>
            Club Operations Manager &nbsp;·&nbsp;
            <span style={{ color: '#FFFF00' }}>Powered by Murko Capital</span>
          </div>
          {showClubSelector ? (
            <select
              className="club-selector"
              value={currentClub?.id ?? ''}
              style={{ maxWidth: 160 }}
              onChange={e => {
                const club = availableClubs.find(c => c.id === e.target.value);
                if (club) setCurrentClub(club);
              }}
            >
              {availableClubs.map(club => (
                <option key={club.id} value={club.id}>{club.name}</option>
              ))}
            </select>
          ) : currentClub ? (
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{currentClub.name}</span>
          ) : null}
        </div>

        {/* Sized up, BK 9/22/26 — first pass was buried at 9px inside the
            tagline row, too small to notice. Now its own proper element. */}
        <Link to="/changelog" style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 15, fontWeight: 700, color: 'var(--yellow, #facc15)',
          textDecoration: 'none', whiteSpace: 'nowrap', marginLeft: 6,
        }}>
          📣 What's New
        </Link>
      </div>

      {/* Right side */}
      <div className="topbar-right" style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        {freshness && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Data Age</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: age.color, fontFamily: 'DM Mono, monospace',
              background: `${age.color}18`, border: `1px solid ${age.color}44`,
              padding: '1px 7px', borderRadius: 4 }}>
              {age.label}
            </span>
          </div>
        )}
        <span className="topbar-role">{role}</span>
        <span className="topbar-user" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{user?.email}</span>
        <button
          className="logout-btn"
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        >
          Sign out
        </button>
      </div>
    </header>
  );
}
